import React from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, IconButton, List, ListItem, ListItemText, ListItemIcon } from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CategoryIcon from '@mui/icons-material/Category';
import TimelineIcon from '@mui/icons-material/Timeline';
import CodeIcon from '@mui/icons-material/Code';
import { SectionTab } from './tab-button';
import { t } from '../config';

const ELEMENT_ICONS = {
  image: ImageIcon,
  text: TextFieldsIcon,
  figure: CategoryIcon,
  line: TimelineIcon,
  svg: CodeIcon,
};

const LayerItem = observer(({ element, store, index }) => {
  const isSelected = store.selectedElementsIds.includes(element.id);
  const Icon = ELEMENT_ICONS[element.type] || CategoryIcon;
  const isLocked = element.locked;

  const handleSelect = () => {
    store.selectElements([element.id]);
  };

  const handleToggleVisibility = (e) => {
    e.stopPropagation();
    element.set({ visible: !element.visible });
    store.history.addUndoState();
  };

  const handleToggleLock = (e) => {
    e.stopPropagation();
    if (isLocked) {
      element.set({
        draggable: true,
        resizable: true,
        contentEditable: true,
        styleEditable: true,
      });
    } else {
      element.set({
        draggable: false,
        resizable: false,
        contentEditable: false,
        styleEditable: false,
      });
    }
    store.history.addUndoState();
  };

  const handleMoveUp = (e) => {
    e.stopPropagation();
    element.moveUp();
    store.history.addUndoState();
  };

  const handleMoveDown = (e) => {
    e.stopPropagation();
    element.moveDown();
    store.history.addUndoState();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    store.deleteElements([element.id]);
    store.history.addUndoState();
  };

  const displayName =
    element.name ||
    `${element.type}${element.text ? `: ${element.text.substring(0, 20)}` : ''}`;

  return (
    <ListItem
      onClick={handleSelect}
      sx={{
        cursor: 'pointer',
        borderRadius: '4px',
        mb: 0.5,
        py: 0.5,
        px: 1,
        backgroundColor: isSelected ? 'rgba(13,131,205,0.1)' : 'transparent',
        border: isSelected ? '1px solid rgba(13,131,205,0.3)' : '1px solid transparent',
        opacity: element.visible ? 1 : 0.5,
        '&:hover': {
          backgroundColor: isSelected
            ? 'rgba(13,131,205,0.15)'
            : 'rgba(0,0,0,0.04)',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>
        <Icon sx={{ fontSize: 18, color: isSelected ? '#0d83cd' : '#666' }} />
      </ListItemIcon>
      <ListItemText
        primary={displayName}
        primaryTypographyProps={{
          variant: 'body2',
          noWrap: true,
          sx: { fontSize: '12px' },
        }}
      />
      <Box sx={{ display: 'flex', gap: 0, ml: 1 }}>
        <IconButton size="small" onClick={handleMoveUp} sx={{ p: 0.3 }}>
          <ArrowUpwardIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton size="small" onClick={handleMoveDown} sx={{ p: 0.3 }}>
          <ArrowDownwardIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton size="small" onClick={handleToggleVisibility} sx={{ p: 0.3 }}>
          {element.visible ? (
            <VisibilityIcon sx={{ fontSize: 14 }} />
          ) : (
            <VisibilityOffIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
        <IconButton size="small" onClick={handleToggleLock} sx={{ p: 0.3 }}>
          {isLocked ? (
            <LockIcon sx={{ fontSize: 14 }} />
          ) : (
            <LockOpenIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
        <IconButton size="small" onClick={handleDelete} sx={{ p: 0.3 }}>
          <DeleteOutlineIcon sx={{ fontSize: 14, color: '#d32f2f' }} />
        </IconButton>
      </Box>
    </ListItem>
  );
});

const LayersPanelContent = observer(({ store }) => {
  const page = store.activePage;

  if (!page || page.children.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('sidePanel.noLayers', 'No layers on this page')}
        </Typography>
      </Box>
    );
  }

  // Show in reverse order (top layer first)
  const elements = [...page.children].reverse();

  return (
    <Box sx={{ p: 1, overflowY: 'auto', height: '100%' }}>
      <Typography variant="subtitle2" sx={{ px: 1, mb: 1 }}>
        {t('sidePanel.layers', 'Layers')} ({page.children.length})
      </Typography>
      <List dense disablePadding>
        {elements.map((element, index) => (
          <LayerItem
            key={element.id}
            element={element}
            store={store}
            index={index}
          />
        ))}
      </List>
    </Box>
  );
});

export const LayersSection = {
  name: 'layers',
  Tab: (props) => (
    <SectionTab name={t('sidePanel.layers', 'Layers')} {...props}>
      <LayersIcon />
    </SectionTab>
  ),
  Panel: LayersPanelContent,
};
